import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  Activity,
  ArrowUpRight,
  Bot,
  CircleCheckBig,
  File,
  Globe,
  LayoutGrid,
  Mail,
  MessageSquare,
  Minus,
  Plus,
  Repeat2,
  Scan,
  Smartphone,
  TrendingUp,
} from 'lucide-react';
import { agentDotShapes, colorForAgent } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { connectorLogoUrl } from '../../../../lib/connectors';
import {
  AGENT_RING_R,
  BRIEF_CARD,
  BRIEF_ID,
  MAESTRO_ID,
  WORLD_CENTER,
  WORLD_H,
  WORLD_W,
  edgePath,
  regionRect,
  type BucketId,
  type MapEdge,
  type MapNode,
  type MapWorld,
  SPACES_Y,
} from '../../../../lib/home-map/map-layout';
import {
  ENTRY_WORLD_HOLD_MS,
  ENTRY_ZOOM_MS,
  clamp,
  easeInOut,
  frame,
  frameWorld,
  lerpView,
  pan,
  toSvgViewBox,
  toWorld,
  zoomAbout,
  type ViewBox,
} from '../../../../lib/home-map/map-camera';
import { bucketOf, type MapEvent } from '../../../../lib/home-map/map-events';
import type { Flight } from '../../../../lib/home-map/use-map-live';
import { Card, IconButton } from '../../ui';
import { MaestroMark } from '../../MaestroMark';
import { OutreachChannelGlyph } from '../../connectors/OutreachChannelGlyph';
import { WixWordmark } from '../../connectors/BrandGlyphs';
import { outreachLogoUrl } from '../../../../lib/home-map/channels';
import { MapInfoCard, type MapCardContent } from './MapInfoCard';
import styles from './HomeMap.module.css';

/**
 * The map canvas — ONE `<svg>` holding the static scene (guide ring, spokes, nodes) and the particles in
 * flight, with the controls in a bar along its foot and the info card floated over it.
 *
 * Performance contract (docs/claude/home2-map.md): the scene is laid out once per `world` identity; the
 * camera is a `viewBox` string that only changes while the user pans/zooms or during the 900 ms entry tween
 * (a rAF loop that STOPS when it lands); every particle is a CSS animation along an `offset-path` whose
 * delay is fixed when it mounts, so a whole reel is one render; the spokes' travelling pulses are infinite
 * CSS animations that cost no React work. The line language is the Connectors & MCP genome's: 1px ink
 * spokes with a signal riding them, dotted grey for an unconnected channel.
 */

export const BUCKET_ICONS: Record<BucketId, typeof File> = {
  tasks: CircleCheckBig,
  goals: TrendingUp,
  routines: Repeat2,
  files: File,
  apps: LayoutGrid,
  messages: Mail,
};

/** What the reader picked on the map: a node, a wire, or a particle (an event). */
export type MapSelection = { kind: 'node'; id: string } | { kind: 'edge'; from: string; to: string } | { kind: 'event'; event: MapEvent };

export interface HomeMapProps {
  world: MapWorld;
  flights: Flight[];
  settle: (id: string) => void;
  /** Where the entry choreography lands: the brief (home) or the agents ring (the Agents destination). */
  landOn: 'brief' | 'agents';
  /** The SMALL state: a section is open under the map, so the map is a short, static band — no controls,
   *  no cards; any click on it goes back to the LARGE state (see `onGroundClick`). */
  compact?: boolean;
  /** A district was clicked: open that Space (the caller navigates). */
  onOpenSpace?: (node: MapNode) => void;
  /** The main agent's name — what the centre node is called (never a hard-coded "Maestro"). */
  maestroName?: string;
  /** An event picked from OUTSIDE the map (a timeline tick): its card opens as if its particle were clicked. */
  picked?: { event: MapEvent; seq: number } | null;
  /** A click on the map's empty ground (or, in the small state, anywhere on it). */
  onGroundClick?: () => void;
  /** The timeline (activity) button. */
  onTimeline: () => void;
  /** A particle just landed on a bucket — the tile row bumps that tile. */
  onLand?: (bucket: BucketId) => void;
  /** What the info card says for a selection: title, region, text, action. Null = nothing to show. */
  describe: (selection: MapSelection) => MapCardContent | null;
  /** The morning meeting card, rendered ON the map above the ring (see MapBriefCard). */
  briefCard: React.ReactNode;
  /** The timeline (play · track · live), rendered in the map's foot beside the control bar. */
  timeline?: React.ReactNode;
  /** A count on a node's corner (replies waiting on the reader, per outreach channel), by node id. */
  badges?: Record<string, number>;
}

const NODE_R = { maestro: 24, agent: 12, small: 16, bucket: 16 } as const;
/** Below this host width the minimap is dropped (it would sit over the connectors row). */
/** A district plate: wide enough for an icon and a Space's name. */
const DISTRICT = { w: 168, h: 44 };
const MINIMAP_MIN_W = 600;
/** Labels sit this far under a node's edge, so a label never runs into the glyph above it. */
const LABEL_GAP = 16;
/** The arrival choreography, in the Connectors & MCP genome's timing: the hub first, then every node flies
 *  out of it in band order (74 ms apart), each spoke drawing itself as its node lands, the signals starting
 *  once the wire is there. */
const FLY_BASE_MS = 180;
const FLY_STAGGER_MS = 74;
const FLY_MS = 720;
const PULSE_STAGGER_MS = 300;

/** A node's place in the arrival stagger: the hub first, then the ring, then the bands top to bottom. */
function orderOf(node: MapNode): number {
  switch (node.region) {
    case 'centre':
      return 0;
    case 'brief':
      return 1;
    case 'agents':
      return 1 + Math.round(Math.abs(node.x - WORLD_CENTER.x) / 40);
    case 'channelsIn':
    case 'outreach':
      return 3 + Math.round(Math.abs(node.y - WORLD_CENTER.y) / 60);
    case 'connectors':
      return 5 + Math.round(Math.abs(node.x - WORLD_CENTER.x) / 120);
    default:
      return 8;
  }
}

function nodeById(world: MapWorld): Record<string, MapNode> {
  return Object.fromEntries(world.nodes.map((n) => [n.id, n]));
}

export function HomeMap({
  world,
  flights,
  settle,
  landOn,
  compact = false,
  onGroundClick,
  onTimeline,
  onLand,
  onOpenSpace,
  describe,
  briefCard,
  timeline,
  badges,
  picked,
  maestroName,
}: HomeMapProps) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [viewport, setViewport] = useState({ w: 0, h: 0 });
  const [view, setView] = useState<ViewBox>(() => frameWorld({ w: 1200, h: 600 }));
  const viewRef = useRef(view);
  viewRef.current = view;
  const [selected, setSelected] = useState<MapSelection | null>(null);
  const [entered, setEntered] = useState(false);
  // A particle that just landed pops its destination: a ripple keyed per landing, so it always replays.
  const [landed, setLanded] = useState<Record<string, number>>({});
  const reducedMotion = useReducedMotion();
  const nodes = useMemo(() => nodeById(world), [world]);
  const tween = useRef<number | null>(null);

  // Viewport size, from the host — the only measurement on this screen.
  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setViewport((prev) => (prev.w === Math.round(width) && prev.h === Math.round(height) ? prev : { w: Math.round(width), h: Math.round(height) }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const stopTween = useCallback(() => {
    if (tween.current !== null) cancelAnimationFrame(tween.current);
    tween.current = null;
  }, []);

  const animateTo = useCallback(
    (target: ViewBox, ms: number) => {
      stopTween();
      if (ms <= 0 || reducedMotion) {
        setView(target);
        return;
      }
      const from = viewRef.current;
      const start = performance.now();
      const step = (now: number) => {
        const tt = Math.min((now - start) / ms, 1);
        setView(lerpView(from, target, easeInOut(tt)));
        if (tt < 1) tween.current = requestAnimationFrame(step);
        else tween.current = null;
      };
      tween.current = requestAnimationFrame(step);
    },
    [reducedMotion, stopTween],
  );

  // The entry choreography: whole world, hold, then land. Once per mount. The hold timer lives in a ref
  // and reads the viewport through a ref, because the viewport is re-measured as the rest of the screen
  // settles (tile counts landing change the map's height) — an effect that owned the timer was cleaned up
  // by that re-measure and the map never landed on the brief. The Agents destination frames the ring at once.
  const viewportRef = useRef(viewport);
  viewportRef.current = viewport;
  const landTarget = useCallback((vp: { w: number; h: number }) => frame(regionRect(landOn === 'brief' ? 'landing' : 'agents'), vp, 0.06), [landOn]);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (entered || viewport.w === 0) return;
    setEntered(true);
    if (compact || landOn === 'agents') {
      setView(landOn === 'agents' ? landTarget(viewport) : frame(regionRect('landing'), viewport, 0.06));
      setSelected(null);
      return;
    }
    setView(frameWorld(viewport));
    holdTimer.current = setTimeout(
      () => {
        holdTimer.current = null;
        animateTo(landTarget(viewportRef.current), ENTRY_ZOOM_MS);
      },
      reducedMotion ? 0 : ENTRY_WORLD_HOLD_MS,
    );
  }, [entered, viewport, compact, landOn, animateTo, landTarget, reducedMotion]);
  useEffect(
    () => () => {
      if (holdTimer.current) clearTimeout(holdTimer.current);
      stopTween();
    },
    [stopTween],
  );

  // The small band carries no card: a selection made on the large map is dropped when a section opens.
  useEffect(() => {
    if (compact) setSelected(null);
  }, [compact]);
  // A tick on the timeline picks its event: the card opens over the node the particle landed on.
  // Each pick carries a sequence number, so the same tick opens its card again; a map that mounts after
  // a pick (the phone's overlay) does not replay it.
  const pickSeen = useRef(picked?.seq ?? 0);
  useEffect(() => {
    if (!picked || compact || picked.seq === pickSeen.current) return;
    pickSeen.current = picked.seq;
    setSelected({ kind: 'event', event: picked.event });
  }, [picked, compact]);

  // Small ⇄ large: re-frame the whole world when the host changes shape. The host's new size
  // arrives a frame or two LATER (the ResizeObserver), so the frame is redone for every measure that lands
  // inside the re-frame window — a frame computed for the old aspect is sliced at the sides by the svg.
  const shapeBefore = useRef(String(compact));
  const reframeUntil = useRef(0);
  useEffect(() => {
    const shape = String(compact);
    if (shapeBefore.current === shape) return;
    shapeBefore.current = shape;
    if (viewportRef.current.w === 0) return;
    // A shape change during the entry hold ends the hold: the map re-frames for its new box and stays there
    // rather than tweening onto the brief inside an expanded map a second later.
    if (holdTimer.current) {
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
    reframeUntil.current = performance.now() + ENTRY_ZOOM_MS;
    // The band shows the meeting and the Maestro (the landing), not the whole world shrunk to a strip.
    animateTo(compact ? frame(regionRect('landing'), viewportRef.current, 0.06) : frameWorld(viewportRef.current), ENTRY_ZOOM_MS / 2);
  }, [compact, animateTo]);
  useEffect(() => {
    const left = reframeUntil.current - performance.now();
    if (left <= 0 || viewport.w === 0) return;
    animateTo(compact ? frame(regionRect('landing'), viewport, 0.06) : frameWorld(viewport), Math.max(160, left / 2));
  }, [viewport, compact, animateTo]);

  // Pan by drag; a click (under 4px of travel) on empty ground clears the selection (and, with a section
  // open, brings the large map back).
  // "Ground" is decided at pointerDOWN: once the svg captures the pointer, every later event is retargeted
  // to the svg itself, so a pointerup's target can no longer tell a node from empty ground.
  const drag = useRef<{ x: number; y: number; moved: boolean; onGround: boolean } | null>(null);
  // Pinch: every active pointer by id; with two down, their distance scales the view about their midpoint
  // and the midpoint's travel pans it — the phone's two-finger zoom. `touch-action: none` on the svg keeps
  // the browser's own page zoom out of it.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ dist: number; mid: { x: number; y: number } } | null>(null);
  const pinchState = (el: SVGSVGElement) => {
    const [a, b] = [...pointers.current.values()];
    const rect = el.getBoundingClientRect();
    return { dist: Math.hypot(b.x - a.x, b.y - a.y), mid: { x: (a.x + b.x) / 2 - rect.left, y: (a.y + b.y) / 2 - rect.top } };
  };
  const onPointerDown = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (e.button !== 0) return;
    stopTween();
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size >= 2) {
      // A second (or third) finger: the drag becomes a pinch, and the tap it might have been is off.
      drag.current = null;
      pinch.current = pinchState(e.currentTarget);
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }
    const target = e.target as Element;
    const onGround = target === e.currentTarget || target.classList.contains(styles.ground);
    drag.current = { x: e.clientX, y: e.clientY, moved: false, onGround };
    // No capture yet: capturing here retargets the pointerup to the svg, and the browser then fires the
    // CLICK at the svg too (the common ancestor of down and up) — so a node's onClick never ran. The
    // pointer is captured only once a drag actually starts (see onPointerMove).
  };
  const onPointerMove = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current && pointers.current.size >= 2) {
      const prev = pinch.current;
      const next = pinchState(e.currentTarget);
      pinch.current = next;
      if (prev.dist > 0 && next.dist > 0) {
        const vp = viewportRef.current;
        setView((v) => {
          const at = toWorld(v, prev.mid, vp);
          const zoomed = zoomAbout(v, next.dist / prev.dist, at);
          return pan(zoomed, next.mid.x - prev.mid.x, next.mid.y - prev.mid.y, vp);
        });
      }
      return;
    }
    const d = drag.current;
    if (!d) return;
    // A pointerup that never reached the svg (a node stopped it, the pointer left the window) must not
    // leave a phantom drag behind: with no button down there is no drag, whatever the ref remembers.
    if (e.buttons === 0) {
      drag.current = null;
      return;
    }
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!d.moved && Math.hypot(dx, dy) < 4) return;
    if (!d.moved) e.currentTarget.setPointerCapture(e.pointerId);
    d.moved = true;
    d.x = e.clientX;
    d.y = e.clientY;
    setView((v) => pan(v, dx, dy, viewport));
  };
  const onPointerUp = (e: ReactPointerEvent<SVGSVGElement>) => {
    pointers.current.delete(e.pointerId);
    if (pinch.current) {
      // The pinch ends when the second finger lifts; the remaining finger does not become a drag or a tap.
      drag.current = null;
      if (pointers.current.size < 2) pinch.current = null;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
      return;
    }
    const d = drag.current;
    drag.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    if (d && !d.moved && (d.onGround || compact)) {
      setSelected(null);
      logPageClickBi({ page_name: 'map_home', button_name: compact ? 'map-enlarge' : 'map-ground' });
      onGroundClick?.();
    }
  };
  // A cancelled pointer (the browser took the gesture) is not a click on the ground.
  const onPointerCancel = (e: ReactPointerEvent<SVGSVGElement>) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    drag.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };
  // Wheel: plain wheel PANS (the natural reading of a scroll gesture on a map); ctrl/⌘ + wheel — which is
  // also what a trackpad pinch arrives as — ZOOMS about the pointer. A NATIVE, non-passive listener: React's
  // onWheel is passive, so it could not preventDefault, and the page would scroll/browser-zoom underneath.
  const svgRef = useRef<SVGSVGElement | null>(null);
  const lastPanAt = useRef(0);
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const vp = viewportRef.current;
      // A pan the camera cannot make (the map is already at that edge) is NOT claimed: the page scrolls
      // instead, so a reader wheeling down the home is not trapped on the map. The check comes before
      // preventDefault, which is what claims the gesture.
      const legacy = (e as WheelEvent & { wheelDeltaY?: number }).wheelDeltaY ?? 0;
      const notchLike = e.deltaMode !== 0 || (e.deltaX === 0 && legacy !== 0 && legacy % 120 === 0);
      const now = performance.now();
      const mouseWheel = notchLike && now - lastPanAt.current > 150;
      const zooming = e.ctrlKey || e.metaKey || mouseWheel;
      if (!zooming) {
        const next = pan(viewRef.current, -e.deltaX, -e.deltaY, vp);
        // Stuck on the axis the gesture is mostly about: a vertical scroll at the bottom edge scrolls the
        // page even if the map could still slide sideways a little.
        const vertical = Math.abs(e.deltaY) >= Math.abs(e.deltaX);
        const stuck = vertical ? Math.abs(next.y - viewRef.current.y) < 0.01 : Math.abs(next.x - viewRef.current.x) < 0.01;
        if (stuck && lastPanAt.current < now - 150) return;
      }
      e.preventDefault();
      // The frame collapses the sidebar on a dominantly-horizontal wheel (a trackpad swipe); a pan across
      // the map is exactly that gesture, so it must not travel past the map.
      e.stopPropagation();
      stopTween();
      // A MOUSE wheel zooms (the map convention on Windows and with any mouse): its deltas come in whole
      // line steps (deltaMode 1) or in 120-multiples of the legacy wheelDelta, with no horizontal part. A
      // trackpad's two-finger scroll (fractional deltas, often with deltaX) pans; its pinch arrives as
      // ctrl+wheel and zooms.
      // A trackpad streams events every ~16 ms, and one in forty of its pixel deltas happens to be a
      // 120-multiple — so a notch-looking event that follows a pan within 150 ms is still the pan. A mouse
      // notch is isolated in time.
      if (!zooming) lastPanAt.current = now;
      if (e.ctrlKey || e.metaKey || mouseWheel) {
        const rect = el.getBoundingClientRect();
        const at = toWorld(viewRef.current, { x: e.clientX - rect.left, y: e.clientY - rect.top }, vp);
        const factor = mouseWheel && !(e.ctrlKey || e.metaKey) ? (e.deltaY < 0 ? 1.25 : 1 / 1.25) : Math.pow(1.01, -e.deltaY);
        setView((v) => zoomAbout(v, factor, at));
        return;
      }
      setView((v) => pan(v, -e.deltaX, -e.deltaY, vp));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [stopTween]);
  const onDoubleClick = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const at = toWorld(viewRef.current, { x: e.clientX - rect.left, y: e.clientY - rect.top }, viewport);
    stopTween();
    setView((v) => zoomAbout(v, 1.6, at));
  };
  const zoomBy = (factor: number, name: 'map-zoom-in' | 'map-zoom-out') => {
    logPageClickBi({ page_name: 'map_home', button_name: name });
    stopTween();
    setView((v) => zoomAbout(v, factor, { x: v.x + v.w / 2, y: v.y + v.h / 2 }));
  };
  const fit = () => {
    logPageClickBi({ page_name: 'map_home', button_name: 'map-fit' });
    animateTo(frameWorld(viewport), ENTRY_ZOOM_MS / 2);
  };
  // In the small state nothing on the map is a target of its own: any click brings the large map back.
  const selectNode = useCallback(
    (node: MapNode) => {
      if (compact) {
        onGroundClick?.();
        return;
      }
      logPageClickBi({ page_name: 'map_home', button_name: `map-node:${node.region}`, answer: node.id });
      // A district is a DOOR, not a thing with a card: clicking it opens the Space's dashboard.
      if (node.region === 'spaces') {
        if (node.href) onOpenSpace?.(node);
        return;
      }
      setSelected((cur) => (cur?.kind === 'node' && cur.id === node.id ? null : { kind: 'node', id: node.id }));
    },
    [logPageClickBi, compact, onGroundClick, onOpenSpace],
  );
  const selectEdge = useCallback(
    (edge: MapEdge) => {
      if (compact) {
        onGroundClick?.();
        return;
      }
      logPageClickBi({ page_name: 'map_home', button_name: 'map-edge', answer: `${edge.from}>${edge.to}` });
      setSelected({ kind: 'edge', from: edge.from, to: edge.to });
    },
    [logPageClickBi, compact, onGroundClick],
  );
  const selectEvent = useCallback(
    (event: MapEvent) => {
      if (compact) {
        onGroundClick?.();
        return;
      }
      logPageClickBi({ page_name: 'map_home', button_name: 'map-particle', answer: event.kind });
      setSelected({ kind: 'event', event });
    },
    [logPageClickBi, compact, onGroundClick],
  );

  const particleLanded = useCallback(
    (flight: Flight) => {
      settle(flight.event.id);
      setLanded((prev) => ({ ...prev, [flight.event.to]: Date.now() }));
      const b = bucketOf(flight.event);
      if (b) onLand?.(b);
    },
    [settle, onLand],
  );

  const toScreen = (p: { x: number; y: number }) => ({ x: ((p.x - view.x) / view.w) * viewport.w, y: ((p.y - view.y) / view.h) * viewport.h });
  const anchorFor = (sel: MapSelection): { x: number; y: number } | null => {
    if (sel.kind === 'node') return nodes[sel.id] ?? null;
    if (sel.kind === 'edge') {
      const a = nodes[sel.from];
      const b = nodes[sel.to];
      return a && b ? { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } : null;
    }
    return nodes[sel.event.to] ?? null;
  };
  const card = selected ? describe(selected) : null;
  const anchor = selected ? anchorFor(selected) : null;

  return (
    <Card
      ref={hostRef as never}
      variant="stone"
      disablePadding
      className={`${styles.host}${compact ? ` ${styles.compact}` : ''}`}
      data-testid="home-map"
    >
      <svg
        ref={svgRef}
        className={styles.svg}
        viewBox={toSvgViewBox(view)}
        preserveAspectRatio="xMidYMid slice"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onDoubleClick={onDoubleClick}
        role="img"
        aria-label={t('home.map.aria')}
      >
        <defs>
          <pattern id="home-map-dots" width="20" height="20" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.8" className={styles.dot} />
          </pattern>
          <clipPath id="home-map-logo-clip">
            <rect x={-NODE_R.small} y={-NODE_R.small} width={NODE_R.small * 2} height={NODE_R.small * 2} rx={9} />
          </clipPath>
        </defs>
        <rect x={-WORLD_W * 3} y={-WORLD_H * 3} width={WORLD_W * 7} height={WORLD_H * 7} fill="url(#home-map-dots)" className={styles.ground} />

        {/* Region labels — small, out of the way, hidden when the band is short. */}
        {!compact && (
          <g className={`${styles.regionLabels} ${styles.late}`} aria-hidden>
            <text transform={`translate(30 ${WORLD_CENTER.y}) rotate(-90)`} textAnchor="middle">
              {t('home.map.channelsIn').toUpperCase()}
            </text>
            <text transform={`translate(${WORLD_W - 30} ${WORLD_CENTER.y}) rotate(90)`} textAnchor="middle">
              {t('home.map.outreach').toUpperCase()}
            </text>
            <text x={WORLD_CENTER.x} y={regionRect('connectors').y + regionRect('connectors').h + 14} textAnchor="middle">
              {t('home.map.connectors').toUpperCase()}
            </text>
            <text x={WORLD_CENTER.x} y={SPACES_Y - 34} textAnchor="middle">
              {t('home.map.spaces').toUpperCase()}
            </text>
          </g>
        )}

        <Wiring world={world} nodes={nodes} reducedMotion={reducedMotion} onSelect={selectEdge} />

        <g className={styles.nodes}>
          {/* The six buckets are never drawn: the entry TILES right under the map are them. Particles still
              fly to a bucket's spot — off the bottom edge, toward the tile they bump. */}
          {world.nodes
            .filter((node) => node.region !== 'buckets')
            .map((node) => (
              <MapNodeView
                key={node.id}
                node={node}
                selected={selected?.kind === 'node' && selected.id === node.id}
                onSelect={selectNode}
                label={nodeLabel(node, t, maestroName)}
                openLabel={t('home.map.openSpace')}
                landedAt={landed[node.id]}
                order={orderOf(node)}
                badge={badges?.[node.id]}
              />
            ))}
        </g>

        <Particles flights={flights} nodes={nodes} reducedMotion={reducedMotion} onLanded={particleLanded} onSelect={selectEvent} />

        {/* The meeting, as a real card on the map. HTML inside the canvas, so it pans and zooms with it. */}
        <foreignObject
          x={WORLD_CENTER.x - BRIEF_CARD.w / 2}
          y={nodes[BRIEF_ID] ? nodes[BRIEF_ID].y - BRIEF_CARD.h / 2 : 0}
          width={BRIEF_CARD.w}
          height={BRIEF_CARD.h}
          className={styles.briefHost}
          onPointerDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          {briefCard}
        </foreignObject>
      </svg>

      {/* The foot — only in the large state: the control bar (activity · + · − · fit) and, beside it, the
          timeline. The small band is a static preview with no chrome. */}
      {!compact && (
        <div className={styles.foot}>
          <Card size="sm" disablePadding className={styles.bar} role="group" aria-label={t('home.map.zoom')}>
            {/* Every control says what it does on hover (`title`), not only to a screen reader. */}
            <IconButton size="small" variant="outlined" aria-label={t('home.map.timeline')} title={t('home.map.timeline')} onClick={onTimeline}>
              <Activity size={16} aria-hidden />
            </IconButton>
            <span className={styles.barGap} />
            <IconButton
              size="small"
              variant="outlined"
              onClick={() => zoomBy(1.5, 'map-zoom-in')}
              aria-label={t('home.map.zoomIn')}
              title={t('home.map.zoomIn')}
            >
              <Plus size={16} aria-hidden />
            </IconButton>
            <IconButton
              size="small"
              variant="outlined"
              onClick={() => zoomBy(1 / 1.5, 'map-zoom-out')}
              aria-label={t('home.map.zoomOut')}
              title={t('home.map.zoomOut')}
            >
              <Minus size={16} aria-hidden />
            </IconButton>
            <IconButton size="small" variant="outlined" onClick={fit} aria-label={t('home.map.fit')} title={t('home.map.fit')}>
              <Scan size={16} aria-hidden />
            </IconButton>
          </Card>
          {timeline && (
            <Card size="sm" disablePadding className={styles.footTimeline}>
              {timeline}
            </Card>
          )}
        </div>
      )}

      {/* No minimap on a narrow pane or a short band: it would cover the connectors row. */}
      {!compact && viewport.w >= MINIMAP_MIN_W && (
        <Minimap
          world={world}
          view={view}
          onMove={(p) => {
            stopTween();
            setView((v) => clamp({ ...v, x: p.x - v.w / 2, y: p.y - v.h / 2 }));
          }}
          onBi={() => logPageClickBi({ page_name: 'map_home', button_name: 'map-minimap' })}
        />
      )}

      {card && anchor && viewport.w > 0 && (
        <MapInfoCard key={card.key} content={card} anchor={toScreen(anchor)} viewport={viewport} onClose={() => setSelected(null)} />
      )}
    </Card>
  );
}

/** The standing wires — genome style — and the signal riding each connected one. */
const Wiring = memo(function Wiring({
  world,
  nodes,
  reducedMotion,
  onSelect,
}: {
  world: MapWorld;
  nodes: Record<string, MapNode>;
  reducedMotion: boolean;
  onSelect: (e: MapEdge) => void;
}) {
  return (
    <g className={styles.edges}>
      <circle cx={WORLD_CENTER.x} cy={WORLD_CENTER.y} r={AGENT_RING_R} className={styles.ring} aria-hidden />
      {world.edges.map((e, i) => {
        const a = nodes[e.from];
        const b = nodes[e.to];
        if (!a || !b) return null;
        const d = edgePath(a, b);
        const into = e.to === MAESTRO_ID;
        const connected = e.style === 'solid';
        const far = nodes[into ? e.from : e.to];
        const stagger = far ? orderOf(far) : i;
        return (
          <g
            key={`${e.from}>${e.to}`}
            className={styles.wire}
            data-connected={connected}
            style={{ '--d': `${FLY_BASE_MS + stagger * FLY_STAGGER_MS}ms` } as CSSProperties}
          >
            {/* A connected spoke DRAWS itself from the far end (pathLength 1 → a unit dash that slides off); a
                dotted one keeps its dash pattern and fades in instead. */}
            <path
              d={d}
              pathLength={connected ? 1 : undefined}
              className={connected ? styles.spokeOn : styles.spokeOff}
              strokeDasharray={connected ? undefined : '2 3'}
            />
            {/* A wide, invisible twin so the wire is clickable without being fat. */}
            <path
              d={d}
              className={styles.spokeHit}
              onClick={(ev) => {
                ev.stopPropagation();
                onSelect(e);
              }}
              role="button"
              tabIndex={-1}
              aria-label={`${a.label} → ${b.label}`}
            />
            {connected && !reducedMotion && (
              <circle
                r={3}
                className={`${styles.pulse} ${into ? styles.pulseInto : styles.pulseOut}`}
                style={{ offsetPath: `path("${d}")`, animationDelay: `${FLY_BASE_MS + FLY_MS + stagger * PULSE_STAGGER_MS}ms` } as CSSProperties}
              />
            )}
          </g>
        );
      })}
    </g>
  );
});

const Particles = memo(function Particles({
  flights,
  nodes,
  reducedMotion,
  onLanded,
  onSelect,
}: {
  flights: Flight[];
  nodes: Record<string, MapNode>;
  reducedMotion: boolean;
  onLanded: (flight: Flight) => void;
  onSelect: (event: MapEvent) => void;
}) {
  return (
    <g className={styles.particles}>
      {flights.map((flight, i) => {
        const a = nodes[flight.event.from];
        const b = nodes[flight.event.to];
        if (!a || !b) return null;
        // Parked particles that share a destination fan out sideways, so each stays visible and clickable.
        const siblings = flight.still ? flights.filter((f) => f.still && f.event.to === flight.event.to) : [];
        const rank = flight.still ? siblings.findIndex((f) => f.event.id === flight.event.id) : 0;
        const fan = flight.still ? (rank - (siblings.length - 1) / 2) * 16 : 0;
        return (
          <Particle
            key={`${flight.event.id}@${flight.launchAt}`}
            flight={flight}
            from={a}
            to={b}
            fan={fan}
            reducedMotion={reducedMotion}
            onLanded={onLanded}
            onSelect={onSelect}
            index={i}
          />
        );
      })}
    </g>
  );
});

/** One particle. Its `--delay` is fixed ONCE, when it mounts: a CSS animation's start is pinned at creation,
 *  so re-deriving the delay from a later `performance.now()` on every render would pull the flight EARLIER
 *  each camera frame and compress the reel during the entry tween. A `still` particle (the scrubber's) is
 *  parked over its destination and stays until the scrub moves on. */
const Particle = memo(function Particle({
  flight,
  from,
  to,
  fan,
  reducedMotion,
  onLanded,
  onSelect,
}: {
  flight: Flight;
  from: MapNode;
  to: MapNode;
  fan: number;
  index: number;
  reducedMotion: boolean;
  onLanded: (flight: Flight) => void;
  onSelect: (event: MapEvent) => void;
}) {
  const [delay] = useState(() => Math.max(0, flight.launchAt - performance.now()));
  const color = flight.event.agentId ? colorForAgent(flight.event.agentId) : 'var(--ds-text-primary)';
  const Icon = iconForKind(flight.event.kind);
  // A parked particle sits over its destination — unless that is a bucket, which is off the map's bottom
  // edge (the tiles are the buckets): then it parks under the node it left, where the reader can click it.
  // Under the origin's LABEL, not over it: an agent's name sits 28 units under its centre.
  const park =
    to.region === 'buckets'
      ? { x: from.x, y: from.y + (from.region === 'centre' ? NODE_R.maestro : NODE_R.agent) + LABEL_GAP + 16 }
      : to.region === 'spaces'
      ? { x: to.x, y: to.y - DISTRICT.h / 2 - 16 }
      : { x: to.x, y: to.y - NODE_R.small - 10 };
  // The flight IS the content of this map, so it plays under reduced motion too (the glow and the
  // heartbeat are what that setting removes). A browser without CSS motion paths on SVG (`offset-path`)
  // flies the dot along the straight line instead of the wire's curve, never parks it unflown.
  const style: CSSProperties & Record<string, string> = flight.still
    ? { transform: `translate(${park.x + fan}px, ${park.y}px)` }
    : MOTION_PATHS
    ? { offsetPath: `path("${edgePath(from, to)}")`, offsetRotate: '0deg', '--delay': `${delay}ms`, '--flight': `${flight.flightMs}ms` }
    : {
        '--x0': `${from.x}px`,
        '--y0': `${from.y}px`,
        '--x1': `${to.x}px`,
        '--y1': `${to.y}px`,
        '--delay': `${delay}ms`,
        '--flight': `${flight.flightMs}ms`,
      };
  return (
    <g
      className={flight.still ? styles.particleParked : MOTION_PATHS ? styles.particle : styles.particleLine}
      style={style}
      onAnimationEnd={() => !flight.still && onLanded(flight)}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(flight.event);
      }}
      role="button"
      tabIndex={-1}
      aria-label={flight.event.text}
    >
      <circle r={18} fill={color} className={styles.particleGlow} />
      <circle r={12} fill={color} className={styles.particleDot} />
      {Icon && (
        <svg x={-7} y={-7} width={14} height={14} viewBox="0 0 24 24" className={styles.particleGlyph}>
          <Icon size={24} aria-hidden />
        </svg>
      )}
    </g>
  );
});

/** Whether this browser animates `offset-distance` on SVG elements (the wire-following flight). */
const MOTION_PATHS =
  typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function' &&
  CSS.supports('offset-path', 'path("M0 0 L1 1")') &&
  CSS.supports('offset-distance', '50%');

const MapNodeView = memo(function MapNodeView({
  node,
  selected,
  onSelect,
  label,
  landedAt,
  order,
  badge,
  openLabel,
}: {
  node: MapNode;
  selected: boolean;
  onSelect: (n: MapNode) => void;
  label: string;
  landedAt?: number;
  order: number;
  badge?: number;
  openLabel?: string;
}) {
  const off = node.connected === false;
  const common = {
    className: `${styles.node}${selected ? ` ${styles.nodeSelected}` : ''}${off ? ` ${styles.nodeOff}` : ''}`,
    transform: `translate(${node.x} ${node.y})`,
    // The fly-in: from the hub's direction (a fraction of the way), staggered by the node's order on its band.
    style: {
      '--fx': `${(WORLD_CENTER.x - node.x) * 0.55}px`,
      '--fy': `${(WORLD_CENTER.y - node.y) * 0.55}px`,
      '--d': `${FLY_BASE_MS + order * FLY_STAGGER_MS}ms`,
    } as CSSProperties,
    onClick: (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      onSelect(node);
    },
    role: 'button' as const,
    tabIndex: 0,
    onKeyDown: (e: { key: string; preventDefault: () => void }) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(node);
      }
    },
  };
  // The ripple a landing particle leaves: re-keyed per landing so it replays; gone once it has run.
  const ripple = landedAt ? <circle key={landedAt} r={NODE_R.small} className={styles.ripple} /> : null;
  switch (node.region) {
    case 'centre':
      return (
        <g {...common} aria-label={label}>
          <g className={styles.fly}>
            <circle r={NODE_R.maestro + 4} className={styles.heartbeat} />
            {ripple}
            <g className={styles.body}>
              <circle r={NODE_R.maestro + 6} className={styles.halo} />
              <svg x={-NODE_R.maestro} y={-NODE_R.maestro} width={NODE_R.maestro * 2} height={NODE_R.maestro * 2} viewBox="0 0 52 52">
                <MaestroMark size={52} />
              </svg>
            </g>
          </g>
        </g>
      );
    case 'agents': {
      const id = node.id.slice('agent:'.length);
      // The agent's own mark — the concentric dot Your Agents and the brief use (`AgentDot`), drawn as
      // native SVG ellipses at the node's size; the same shapes on iOS and Android.
      const k = NODE_R.agent / 32;
      return (
        <g {...common} aria-label={label}>
          <g className={styles.fly}>
            {ripple}
            <g className={styles.body}>
              <circle r={NODE_R.agent + 6} className={styles.halo} />
              <circle r={NODE_R.agent + 2} className={styles.agentPlate} />
              {agentDotShapes(id).map((sh, i) => (
                <ellipse key={i} cx={(sh.cx - 32) * k} cy={(sh.cy - 32) * k} rx={sh.rx * k} ry={sh.ry * k} fill={sh.fill} />
              ))}
            </g>
            <text y={NODE_R.agent + LABEL_GAP} textAnchor="middle" className={styles.nodeLabel}>
              {node.label}
            </text>
          </g>
        </g>
      );
    }
    case 'brief':
      // The meeting is drawn as a card in a foreignObject (see HomeMap); the node only anchors its wire.
      return null;
    case 'buckets': {
      const Icon = BUCKET_ICONS[node.label as BucketId] ?? File;
      return (
        <g {...common} aria-label={label}>
          <g className={styles.fly}>
            {ripple}
            <g className={styles.body}>
              <circle r={NODE_R.bucket + 6} className={styles.halo} />
              <circle r={NODE_R.bucket} className={styles.bucket} data-bucket={node.label} />
              <svg x={-8} y={-8} width={16} height={16} viewBox="0 0 24 24" className={styles.bucketGlyph}>
                <Icon size={24} aria-hidden />
              </svg>
            </g>
            <text y={NODE_R.bucket + LABEL_GAP} textAnchor="middle" className={styles.nodeLabel}>
              {label}
            </text>
          </g>
        </g>
      );
    }
    case 'spaces': {
      // A DISTRICT: a wide plate carrying the Space's icon and name — the ground its work lands on. Clicking
      // it opens the Space.
      const w = DISTRICT.w;
      const h = DISTRICT.h;
      return (
        <g {...common} aria-label={label} className={`${common.className as string} ${styles.district}`}>
          <g className={styles.fly}>
            {ripple}
            <g className={styles.body}>
              <rect x={-w / 2 - 6} y={-h / 2 - 6} width={w + 12} height={h + 12} rx={16} className={styles.halo} />
              <rect x={-w / 2} y={-h / 2} width={w} height={h} rx={12} className={styles.districtPlate} />
              {node.icon && /^https?:\/\//.test(node.icon) ? (
                <image href={node.icon} x={-w / 2 + 8} y={-10} width={20} height={20} preserveAspectRatio="xMidYMid meet" />
              ) : node.icon && node.icon.length <= 4 ? (
                <text x={-w / 2 + 18} y={6} className={styles.districtIcon}>
                  {node.icon}
                </text>
              ) : (
                <svg x={-w / 2 + 12} y={-8} width={16} height={16} viewBox="0 0 24 24" className={styles.districtGlyph}>
                  <LayoutGrid size={24} aria-hidden />
                </svg>
              )}
              {/* The name, and under it what a click does — the arrow alone did not say. */}
              <text x={-w / 2 + 38} y={0} textAnchor="start" className={styles.districtLabel}>
                {label}
              </text>
              <text x={-w / 2 + 38} y={13} textAnchor="start" className={styles.districtHint}>
                {openLabel}
              </text>
              <svg x={w / 2 - 24} y={-7} width={14} height={14} viewBox="0 0 24 24" className={styles.districtArrow}>
                <ArrowUpRight size={24} aria-hidden />
              </svg>
            </g>
          </g>
        </g>
      );
    }
    case 'connectors':
      if (node.action === 'connect') {
        return (
          <g {...common} aria-label={label}>
            <g className={styles.fly}>
              <g className={styles.body}>
                <rect
                  x={-NODE_R.small - 6}
                  y={-NODE_R.small - 6}
                  width={(NODE_R.small + 6) * 2}
                  height={(NODE_R.small + 6) * 2}
                  rx={12}
                  className={styles.halo}
                />
                <rect
                  x={-NODE_R.small}
                  y={-NODE_R.small}
                  width={NODE_R.small * 2}
                  height={NODE_R.small * 2}
                  rx={9}
                  className={styles.tileAction}
                  strokeDasharray="3 3"
                />
                <svg x={-8} y={-8} width={16} height={16} viewBox="0 0 24 24" className={styles.tileActionGlyph}>
                  <Plus size={24} aria-hidden />
                </svg>
              </g>
              <text y={NODE_R.small + LABEL_GAP} textAnchor="middle" className={styles.nodeLabel}>
                {label}
              </text>
            </g>
          </g>
        );
      }
      // The connector's OWN logo when the connectors screen has one (that is the icon it shows), else the
      // logo service by slug; an MCP server has neither and shows its letter.
      return (
        <TileNode
          node={node}
          common={common}
          label={label}
          logo={node.slug?.startsWith('mcp:') ? null : node.logoUrl ?? connectorLogoUrl(node.slug ?? '')}
          ripple={ripple}
        />
      );
    case 'outreach': {
      // A branded channel wears its logo; email, SMS and phone keep a glyph.
      const brand = outreachLogoUrl(node.slug ?? '');
      return (
        <TileNode
          node={node}
          common={common}
          label={label}
          logo={brand}
          glyph={brand ? undefined : <OutreachChannelGlyph slug={node.slug ?? ''} />}
          ripple={ripple}
          badge={badge}
        />
      );
    }
    default: {
      // Symphony's own surfaces and the AI-coder clients have no vendor logo — a glyph each.
      const glyph = channelGlyph(node.slug ?? '');
      if (glyph) return <TileNode node={node} common={common} label={label} logo={null} glyph={glyph} ripple={ripple} />;
      return <TileNode node={node} common={common} label={label} logo={node.logoUrl ?? connectorLogoUrl(node.slug ?? '')} ripple={ripple} />;
    }
  }
});

/** The glyph for a channel Symphony hears you through that has no brand logo. */
function channelGlyph(slug: string): React.ReactNode | null {
  if (slug === 'email-in') return <Mail size={24} aria-hidden />;
  if (slug === 'sms-in') return <MessageSquare size={24} aria-hidden />;
  if (slug === 'web') return <Globe size={24} aria-hidden />;
  if (slug === 'phone-app') return <Smartphone size={24} aria-hidden />;
  if (slug === 'symphony-apps') return <LayoutGrid size={24} aria-hidden />;
  if (slug.startsWith('coder:')) return <Bot size={24} aria-hidden />;
  return null;
}

/** A channel or tool: the genome's badge — a bright white plate with a hairline when connected, the recessed
 *  grey plate when not — carrying the logo (or a glyph). The name sits BESIDE a side-column tile (left of a
 *  channel in, right of an outreach channel — eight of them stack too tightly for labels underneath) and
 *  UNDER a tool in the bottom row. */
function TileNode({
  node,
  common,
  label,
  logo,
  glyph,
  ripple,
  badge,
}: {
  node: MapNode;
  common: Record<string, unknown>;
  label: string;
  logo: string | null;
  glyph?: React.ReactNode;
  ripple: React.ReactNode;
  badge?: number;
}) {
  const [logoFailed, setLogoFailed] = useState(false);
  // A new logo URL (a tool reconnected) gets its own chance to load.
  useEffect(() => setLogoFailed(false), [logo]);
  const side = node.region === 'channelsIn' ? 'start' : node.region === 'outreach' ? 'end' : 'under';
  const labelProps =
    side === 'start'
      ? { x: -NODE_R.small - 10, y: 4, textAnchor: 'end' as const }
      : side === 'end'
      ? { x: NODE_R.small + 10, y: 4, textAnchor: 'start' as const }
      : { x: 0, y: NODE_R.small + LABEL_GAP, textAnchor: 'middle' as const };
  const on = node.connected !== false;
  return (
    <g {...common} aria-label={label}>
      <g className={styles.fly}>
        {ripple}
        <g className={styles.body}>
          <rect
            x={-NODE_R.small - 6}
            y={-NODE_R.small - 6}
            width={(NODE_R.small + 6) * 2}
            height={(NODE_R.small + 6) * 2}
            rx={12}
            className={styles.halo}
          />
          <rect
            x={-NODE_R.small}
            y={-NODE_R.small}
            width={NODE_R.small * 2}
            height={NODE_R.small * 2}
            rx={9}
            className={on ? styles.plateOn : styles.plateOff}
          />
          {node.folded ? (
            <text y={5} textAnchor="middle" className={styles.tileText}>
              {node.label}
            </text>
          ) : glyph ? (
            <svg x={-9} y={-9} width={18} height={18} viewBox="0 0 24 24" className={styles.tileGlyph}>
              {glyph}
            </svg>
          ) : node.slug === 'wix' ? (
            <svg x={-11} y={-4.5} width={22} height={9} viewBox="0 0 28.9271 11.1995" className={styles.tileGlyph}>
              <WixWordmark width={28.9271} />
            </svg>
          ) : logo && !logoFailed ? (
            <image
              href={logo}
              x={-NODE_R.small + 5}
              y={-NODE_R.small + 5}
              width={NODE_R.small * 2 - 10}
              height={NODE_R.small * 2 - 10}
              clipPath="url(#home-map-logo-clip)"
              preserveAspectRatio="xMidYMid meet"
              // A logo the network never delivers (a blocked host, data saver) leaves a blank plate: fall
              // back to the tool's initial rather than an empty square.
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <text y={5} textAnchor="middle" className={styles.tileText}>
              {node.label.slice(0, 1).toUpperCase()}
            </text>
          )}
        </g>
        <text {...labelProps} className={styles.nodeLabel}>
          {label}
        </text>
        {/* Replies waiting on the reader on this channel, on the tile's corner. */}
        {badge ? (
          <g transform={`translate(${NODE_R.small - 2} ${-NODE_R.small + 2})`} aria-hidden>
            <circle r={8} className={styles.nodeBadge} />
            <text y={3.5} textAnchor="middle" className={styles.nodeBadgeText}>
              {badge > 9 ? '9+' : badge}
            </text>
          </g>
        ) : null}
      </g>
    </g>
  );
}

function Minimap({ world, view, onMove, onBi }: { world: MapWorld; view: ViewBox; onMove: (p: { x: number; y: number }) => void; onBi: () => void }) {
  const { t } = useTranslation();
  const W = 132;
  const H = (W * WORLD_H) / WORLD_W;
  const dragging = useRef(false);
  const toWorldPoint = (e: React.PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * WORLD_W, y: ((e.clientY - r.top) / r.height) * WORLD_H };
  };
  return (
    <svg
      className={styles.minimap}
      width={W}
      height={H}
      viewBox={`0 0 ${WORLD_W} ${WORLD_H}`}
      role="button"
      tabIndex={0}
      aria-label={t('home.map.minimap')}
      onPointerDown={(e) => {
        dragging.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        onBi();
        onMove(toWorldPoint(e));
      }}
      onPointerMove={(e) => {
        if (dragging.current) onMove(toWorldPoint(e));
      }}
      onPointerUp={(e) => {
        dragging.current = false;
        e.currentTarget.releasePointerCapture(e.pointerId);
      }}
      onPointerCancel={() => {
        dragging.current = false;
      }}
    >
      <defs>
        <clipPath id="home-map-minimap-clip">
          <rect width={WORLD_W} height={WORLD_H} />
        </clipPath>
      </defs>
      <rect width={WORLD_W} height={WORLD_H} className={styles.minimapGround} />
      {/* No buckets: they are not drawn on the map either (the tiles are them). */}
      {world.nodes
        .filter((n) => n.region !== 'buckets')
        .map((n) => (
          <circle key={n.id} cx={n.x} cy={n.y} r={n.region === 'centre' ? 22 : 12} className={styles.minimapNode} />
        ))}
      <rect x={view.x} y={view.y} width={view.w} height={view.h} className={styles.minimapView} clipPath="url(#home-map-minimap-clip)" />
    </svg>
  );
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const update = () => setReduced(mq.matches);
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return reduced;
}

function iconForKind(kind: MapEvent['kind']) {
  switch (kind) {
    case 'file':
      return File;
    case 'task':
      return CircleCheckBig;
    case 'goal':
      return TrendingUp;
    case 'routine':
      return Repeat2;
    case 'app':
      return LayoutGrid;
    case 'outreach':
    case 'message':
    case 'notification':
      return Mail;
    default:
      return null;
  }
}

/** The label under a node: buckets and the connect-more node are localized; everything else says its name. */
export function nodeLabel(node: MapNode, t: (k: string) => string, maestroName?: string): string {
  if (node.region === 'buckets') return t(`home.map.bucket.${node.label}`);
  if (node.region === 'spaces') return node.label.length > 18 ? `${node.label.slice(0, 17)}…` : node.label;
  if (node.action === 'connect') return t('home.map.connectMore');
  if (node.id === MAESTRO_ID) return maestroName ?? t('home.map.maestro');
  if (node.id === BRIEF_ID) return t('home.map.brief');
  return shortLabel(node.label);
}

/** A node's name at map size: an MCP server that only has a URL for a name shows its host's first label;
 *  anything longer than ~14 characters is ellipsised (the card shows the full name). */
export function shortLabel(label: string): string {
  let text = label.trim();
  const url = /^(?:https?:\/\/)?([a-z0-9-]+)(?:\.[a-z0-9-]+)+/i.exec(text);
  if (url && text.includes('.') && !text.includes(' ')) text = url[1];
  return text.length > 14 ? `${text.slice(0, 13)}…` : text;
}
